import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatListModule } from '@angular/material/list';
import { MatLineModule } from '@angular/material/core';
import { RequiredMarkerDirective } from '../../shared/directives/form.directive';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { DetailsCardContainerComponent } from '../../shared/cards/details-card-container/details-card-container.component';
import { AnimatedDelayDirective, AnimatedDirective } from '../../shared/directives/animated.directive';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PropertyManagementCompanyDataService } from '../../../../services/property-management-company/property-manager-company-data.service';
import { MatIconModule } from '@angular/material/icon';
import { ButtonDirective, ButtonSpacerDirective } from '../../shared/directives/button.directive';
import { AvatarBlockModule } from '../../shared/avatar-block/avatar-block.module';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FlexSpacerDirective } from '../../shared/directives/flex-spacer.directive';
import { Router, RouterLink } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AsyncPipe, I18nPluralPipe, NgForOf, NgIf } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { I18nMap } from '../../../../models/entities/i18n-map';
import { LegfiPhonePipeModule } from '../../../../pipes/format/legfi-phone.module';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CardNestedCardChecklistDirective, CardNestedCardDirective, CardWithFooterDirective } from '../../shared/cards/card.directive';
import { NgxMaskModule } from 'ngx-mask';
import { EmptyStateModule } from '../../shared/empty-state/empty-state.module';
import { TeamMemberAddCardComponent, TeamMemberFormGroup } from '../../memberships/team/team-member-add/team-member-add-card/team-member-add-card.component';
import { avatarInitials } from '../../../../models/entities/base/avatar';
import { ControlMessagesModule } from '../../../shared/control-messages/control-messages.module';
import { validateRecursive } from '../../../../directives/validators/validate-recursive';
import { GrowlerService } from '../../../../services/growler.service';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha';
import environment from '../../../../../environments/environment';
import { AuthService } from '../../../../services/auth/auth.service';
import { of as observableOf, pairwise, startWith } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { OrganizationDataService } from '../../../../services/organization/organization-data.service';
import { MatRadioModule } from '@angular/material/radio';
import { PropertyManagementCompanyService } from '../../../../services/property-management-company/property-management-company.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { TeamMemberSearchComponent } from '../../memberships/team/team-member-search/team-member-search.component';
import { User } from '../../../../models/entities/user';
import { RouterButtonValue } from '../../../../models/entities/router-button-value';

export enum CopiedResourceTypes
{
    DOCUMENTS = 'Documents',
    CHART_OF_ACCOUNTS = 'Chart of accounts',

    // TODO: support additional copy resources here to select/execute
    // VIOLATION_STATUSES = 'Violation statuses',
    // REQUEST_FORMS = 'Request forms',
}

export interface CopiedResourceValue
{
    type: CopiedResourceTypes;
    label: string;
    copySrc: number;
    selected: boolean;
}

export interface CopiedResourceFormGroup
{
    type: FormControl<string>;
    label: FormControl<string>;
    copySrc: FormControl<number>;
    selected: FormControl<boolean>;
}

export interface AddOrganizationFormGroup
{
    name: FormControl<string>;
    copyResources: FormControl<boolean>;
    copiedResources: FormArray<FormGroup<CopiedResourceFormGroup>>;
    admins: FormArray<FormGroup<TeamMemberFormGroup>>;
    captchaToken: FormControl<string>;
}

@UntilDestroy()
@Component({
    selector: 'app-organization-add',
    templateUrl: './organization-add.component.html',
    standalone: true,
    imports: [
        MatCardModule,
        MatProgressBarModule,
        MatListModule,
        MatLineModule,
        PageHeaderComponent,
        DetailsCardContainerComponent,
        AnimatedDirective,
        AnimatedDelayDirective,
        ReactiveFormsModule,
        MatIconModule,
        ButtonSpacerDirective,
        AvatarBlockModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        FlexSpacerDirective,
        RouterLink,
        MatToolbarModule,
        MatProgressSpinnerModule,
        MatAutocompleteModule,
        NgForOf,
        I18nPluralPipe,
        LegfiPhonePipeModule,
        MatSlideToggleModule,
        CardWithFooterDirective,
        NgxMaskModule,
        EmptyStateModule,
        TeamMemberAddCardComponent,
        ButtonDirective,
        ControlMessagesModule,
        NgIf,
        RecaptchaModule,
        MatRadioModule,
        CardNestedCardDirective,
        MatCheckboxModule,
        MatSelectModule,
        AsyncPipe,
        CardNestedCardChecklistDirective,
        TeamMemberSearchComponent,
        RequiredMarkerDirective,
    ],
})
export class OrganizationAddComponent implements OnInit
{
    @ViewChild(TeamMemberAddCardComponent) teamMemberAddCardRef: TeamMemberAddCardComponent;
    @ViewChild(RecaptchaComponent) recaptchaRef: RecaptchaComponent;

    formGroup: FormGroup<AddOrganizationFormGroup> = this._fb.nonNullable.group({
        name: ['', Validators.required],
        copyResources: [false],
        copiedResources: this._fb.array<FormGroup<CopiedResourceFormGroup>>([]),
        admins: this._fb.array<FormGroup<TeamMemberFormGroup>>([], Validators.required),
        captchaToken: environment.production ? ['', Validators.required] : [null],
    });

    canCopyResources = false;
    defaultSelectionOrgId: number;
    savingOrganization = false;
    routerBackButton: RouterButtonValue;

    readonly organizationsMapping: I18nMap = {
        '=0': 'No organizations',
        '=1': '1 organization',
        other: '# organizations',
    };
    readonly managersMapping: I18nMap = {
        '=0': 'No managers',
        '=1': '1 manager',
        other: '# managers',
    };
    readonly initials = avatarInitials;
    readonly environment = environment;
    readonly copiedResourceTypes = CopiedResourceTypes;

    constructor(
            private _fb: FormBuilder,
            private _authService: AuthService,
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _organizationDataService: OrganizationDataService,
            private _router: Router,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    get orgs$() {
        return this._organizationDataService.orgList$;
    }

    get admins() {
        return this.formGroup?.get('admins') as FormArray<FormGroup<TeamMemberFormGroup>>;
    }

    get copiedResources() {
        return this.formGroup.get('copiedResources') as FormArray<FormGroup<CopiedResourceFormGroup>>;
    }

    ngOnInit() {
        this.routerBackButton = {
            label: 'Back',
            route: `/pmc/${this.pmcId}/communities`,
            ariaLabel: 'Go back to communities',
        };

        this.orgs$.pipe(untilDestroyed(this)).subscribe({
            next: (data) => {
                this.canCopyResources = data.length > 0;
                if (this.canCopyResources) {
                    this.defaultSelectionOrgId = data[0].id;
                }
            },
        });

        this.formGroup.get('copyResources').valueChanges.pipe(
                startWith(this.formGroup.get('copyResources').value),
                pairwise(),
                untilDestroyed(this),
        ).subscribe({
            next: ([previousValue, newValue]) => {
                if (newValue !== previousValue) {
                    if (newValue) {
                        for (const type of Object.keys(this.copiedResourceTypes).filter((key) => isNaN(Number(key)))) {
                            const label: string = this.copiedResourceTypes[type];
                            this.copiedResources.push(this._fb.group({
                                type: [type],
                                label: [label],
                                copySrc: [this.defaultSelectionOrgId],
                                selected: [false],
                            }));
                        }
                    } else {
                        this.copiedResources.clear();
                    }
                }
            }
        });
    }

    teamMemberLoaded(users: User[]) {
        // on initial load of available search, if logged-in-user is available, automatically preselect for new community.
        const jwt = LegFiJwtService.read();
        const preselectedUser = users.find((user) => user.id === jwt.id);
        if (!!preselectedUser) {
            this.teamMemberAddCardRef.addMember(preselectedUser);
        }
    }

    teamMemberSelected(user: User) {
        this.teamMemberAddCardRef.addMember(user);
    }

    resolved(token: string) {
        this.formGroup.get('captchaToken').setValue(token);
    }

    submit() {
        validateRecursive(this.formGroup);

        if (this.formGroup.invalid) {
            this._growler.oops('Please double check all required fields');
            return;
        }

        const copiedResources = this.copiedResources.controls.filter((group) => !!group.get('selected').value).map((group) => {
            return { org: group.get('copySrc').value, type: group.get('type').value };
        });

        // use get raw value here to include admins with "disabled" data that already exists
        const groupAdmins = this.admins.getRawValue()
                .filter((admin) => !!admin.isSelected)
                .map((admin) => {
            return {
                id: admin.id || null,
                fullName: admin.fullName,
                email: admin.email,
                role: admin.role,
                phone: admin.phone,
                emailNotification: admin.emailNotification,
            };
        });

        if (groupAdmins.length === 0) {
            this._growler.oops('Please double check all required fields');
            this.admins.setErrors({ required: true });
            return;
        }

        const newGroupRequest = {
            groupName: this.formGroup.get('name').value,
            groupAdmins,
            copiedResources,
            captcha: this.formGroup.get('captchaToken').value,
        };

        this.savingOrganization = true;
        this._authService.registerFromPmc(this.pmcId, newGroupRequest).pipe(
                tap(() => this._propertyManagementCompanyService.getPropertyManagementOrganizationList()),
                switchMap((res) => {
                    if (res.hasOwnProperty('token')) {
                        LegFiJwtService.store(res['token']);

                        const jwt = LegFiJwtService.read();
                        return this._organizationDataService.switchOrganization(jwt.orgId)
                                .pipe(map(() => []));
                    } else {
                        return observableOf(null);
                    }
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => {
                this._growler.success('Success', `${this.formGroup.get('name').value} added`);
                this.recaptchaRef?.reset();

                this._organizationDataService.toggleDemoMode(false);
                this.savingOrganization = false;
                if (res) {
                    return this._router.navigate(['app/launchpad'], { queryParams: { internal: true } });
                } else {
                    return this._router.navigate([this.routerBackButton.route]);
                }
            },
            error: () => {
                this._growler.oops('Unable to add community');
                this.recaptchaRef?.reset();
                this.savingOrganization = false;
            },
        });
    }
}
